"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useEffect, useRef, useCallback } from "react";
import dynamic from "next/dynamic";
import { motion, AnimatePresence } from "framer-motion";
import { ShimmeringText } from "@/components/ui/shimmering-text";
import { MobileTooltip, MobileTooltipContent, MobileTooltipTrigger } from "@/components/ui/mobile-tooltip";
const LivelineChart = dynamic(() => import("liveline").then(mod => ({
  default: mod.Liveline
})), {
  ssr: false,
  loading: () => <div className="h-full w-full" />
});
type Point = {
  time: number;
  value: number;
};
const PLAN_RATES: Record<string, {
  min: number;
  max: number;
}> = {
  glory: {
    min: 250,
    max: 350
  },
  grow: {
    min: 150,
    max: 220
  },
  go: {
    min: 80,
    max: 120
  },
  trial: {
    min: 30,
    max: 60
  }
};
const IDLE_RATE = {
  min: 5,
  max: 15
};
const AI_ACTIVITY_LABELS = ["Scanning niche", "Mapping audience", "Scoring relevance", "Processing signals", "Analyzing trends"];
function generateValue(center: number, range: number, elapsed: number, noiseScale: number): number {
  const drift = Math.sin(elapsed * 0.0008) * range * 0.35;
  const wobble = Math.sin(elapsed * 0.002) * range * 0.15;
  const noise = (Math.random() - 0.5) * noiseScale;
  return Math.max(1, Math.round(center + drift + wobble + noise));
}
export function AIActivityCard(t0) {
  const $ = _c(41);
  const {
    accountType,
    active
  } = t0;
  const rate = active ? PLAN_RATES[accountType] || PLAN_RATES.trial : IDLE_RATE;
  const center = (rate.min + rate.max) / 2;
  const range = rate.max - rate.min;
  const startRef = useRef(0);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [data, setData] = useState(t1);
  const [value, setValue] = useState(center);
  const [loading, setLoading] = useState(true);
  const [hoveredValue, setHoveredValue] = useState(null);
  const [labelIndex, setLabelIndex] = useState(0);
  let t2;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const timeout = setTimeout(() => setLoading(false), 800);
      return () => clearTimeout(timeout);
    };
    t3 = [];
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[3] !== active) {
    t4 = () => {
      if (!active) {
        return;
      }
      const interval = setInterval(() => {
        setLabelIndex(_temp);
      }, 3000);
      return () => clearInterval(interval);
    };
    t5 = [active];
    $[3] = active;
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[6] !== center || $[7] !== range) {
    t6 = () => {
      startRef.current = Date.now();
      const now = Date.now() / 1000;
      const points = [];
      for (let i_0 = 50; i_0 >= 0; i_0--) {
        points.push({
          time: now - i_0 * 2.5,
          value: generateValue(center, range, Date.now() - i_0 * 2500, range * 0.3)
        });
      }
      setData(points);
      setValue(points[points.length - 1].value);
      const interval_0 = setInterval(() => {
        const t = Date.now() / 1000;
        const elapsed = Date.now() - startRef.current;
        const newVal = generateValue(center, range, elapsed, range * 0.3);
        setData(prev => [...prev.slice(-100), {
          time: t,
          value: newVal
        }]);
        setValue(newVal);
      }, 2500);
      return () => clearInterval(interval_0);
    };
    t7 = [center, range];
    $[6] = center;
    $[7] = range;
    $[8] = t6;
    $[9] = t7;
  } else {
    t6 = $[8];
    t7 = $[9];
  }
  useEffect(t6, t7);
  const accentColor = active ? "#10b981" : "#9ca3af";
  const displayValue = hoveredValue ?? value;
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = point => {
      setHoveredValue(point ? Math.round(point.value) : null);
    };
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const handleHover = t8;
  let t9;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <span className="whitespace-nowrap text-xs font-medium uppercase tracking-wide text-gray-400">AI Activity</span>;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== displayValue) {
    t10 = <span className="text-[22px] font-bold tabular-nums text-gray-900">{displayValue}</span>;
    $[12] = displayValue;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <span className="text-[11px] font-medium text-gray-400">/hr</span>;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== t10) {
    t12 = <MobileTooltipTrigger asChild={true}><div className="flex min-w-0 shrink-0 cursor-help flex-col">{t9}<div className="flex items-baseline gap-1">{t10}{t11}</div></div></MobileTooltipTrigger>;
    $[15] = t10;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  const t13 = active ? "Your AI is actively engaging with potential followers \u2014 targeting accounts in your niche, liking content, and connecting with users most likely to follow back. Activity rate varies by plan." : "Growth is paused. Your AI is monitoring your account at a reduced rate. Resume growth to start full-speed engagement again.";
  let t14;
  if ($[17] !== t13) {
    t14 = <MobileTooltipContent side="bottom" className="max-w-[300px]"><p className="text-sm leading-relaxed text-gray-600">{t13}</p></MobileTooltipContent>;
    $[17] = t13;
    $[18] = t14;
  } else {
    t14 = $[18];
  }
  let t15;
  if ($[19] !== t12 || $[20] !== t14) {
    t15 = <MobileTooltip delayDuration={0} disableHoverableContent={true}>{t12}{t14}</MobileTooltip>;
    $[19] = t12;
    $[20] = t14;
    $[21] = t15;
  } else {
    t15 = $[21];
  }
  let t16;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = {
      top: 4,
      right: 48,
      bottom: 4,
      left: 4
    };
    $[22] = t16;
  } else {
    t16 = $[22];
  }
  let t17;
  if ($[23] !== accentColor || $[24] !== data || $[25] !== loading || $[26] !== value) {
    t17 = <div className="h-10 min-w-0 flex-1"><LivelineChart data={data} value={value} loading={loading} theme="light" color={accentColor} grid={false} badge={true} badgeVariant="minimal" badgeTail={true} fill={true} pulse={true} momentum={true} scrub={true} exaggerate={true} window={60} lerpSpeed={0.12} formatValue={_temp2} onHover={handleHover} cursor="default" padding={t16} /></div>;
    $[23] = accentColor;
    $[24] = data;
    $[25] = loading;
    $[26] = value;
    $[27] = t17;
  } else {
    t17 = $[27];
  }
  const t18 = active ? AI_ACTIVITY_LABELS[labelIndex] : "paused";
  let t19;
  let t20;
  let t21;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = {
      opacity: 0,
      y: "100%"
    };
    t20 = {
      opacity: 1,
      y: 0
    };
    t21 = {
      opacity: 0,
      y: "-100%"
    };
    $[28] = t19;
    $[29] = t20;
    $[30] = t21;
  } else {
    t19 = $[28];
    t20 = $[29];
    t21 = $[30];
  }
  let t22;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = {
      duration: 0.25,
      ease: [0.4, 0, 0.2, 1]
    };
    $[31] = t22;
  } else {
    t22 = $[31];
  }
  const t23 = active ? `${AI_ACTIVITY_LABELS[labelIndex]}...` : "Paused";
  let t24;
  if ($[32] !== t23) {
    t24 = <ShimmeringText text={t23} duration={0.8} wave={true} color="#374151" shimmeringColor="#9ca3af" />;
    $[32] = t23;
    $[33] = t24;
  } else {
    t24 = $[33];
  }
  let t25;
  if ($[34] !== t18 || $[35] !== t24) {
    t25 = <div className="relative hidden h-[20px] w-[160px] shrink-0 overflow-hidden lg:block"><AnimatePresence mode="popLayout"><motion.span key={t18} initial={t19} animate={t20} exit={t21} transition={t22} className="absolute inset-0 flex items-center whitespace-nowrap text-[14px] font-semibold">{t24}</motion.span></AnimatePresence></div>;
    $[34] = t18;
    $[35] = t24;
    $[36] = t25;
  } else {
    t25 = $[36];
  }
  let t26;
  if ($[37] !== t15 || $[38] !== t17 || $[39] !== t25) {
    t26 = <div className="card !mb-0 flex min-w-0 basis-full items-center gap-3 overflow-hidden !p-3 !px-4 !py-3 lg:basis-auto lg:flex-1">{t15}{t17}{t25}</div>;
    $[37] = t15;
    $[38] = t17;
    $[39] = t25;
    $[40] = t26;
  } else {
    t26 = $[40];
  }
  return t26;
}
function _temp2(v) {
  return Math.round(v).toString();
}
function _temp(i) {
  return (i + 1) % AI_ACTIVITY_LABELS.length;
}